import { Component } from '@theme/component';

/**
 * Counts down to a deadline inside an announcement slide.
 *
 * The slide's rich text carries `(days)`, `(hours)` and `(mins)` tokens, which the block
 * swaps for the refs below before rendering. Each part hides itself while its value is zero,
 * so "2 days 4 hours 10 mins" collapses to "10 mins" as the deadline approaches, and the
 * whole countdown empties out once it passes.
 *
 * The element is a component in its own right so its refs stay out of the surrounding
 * `announcement-bar-component`, which collects slides from the same subtree.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [mins]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementCountdown extends Component {
  /** @type {number | undefined} */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    if (Number.isNaN(this.deadline)) return;

    this.#tick();
    this.#interval = setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  /**
   * The target time, in milliseconds since the epoch.
   *
   * Merchants enter `YYYY-MM-DD HH:MM`, which browsers parse inconsistently, so the space is
   * normalised to the `T` separator that `Date` handles the same way everywhere. Without a
   * timezone the value is read as the visitor's local time, matching the legacy behaviour.
   *
   * @returns {number}
   */
  get deadline() {
    const value = (this.dataset.countdownTime ?? '').trim();

    if (!value) return NaN;

    return new Date(value.replace(' ', 'T')).getTime();
  }

  #stop() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  #tick = () => {
    const remaining = this.deadline - Date.now();

    // A deadline edited to something unparseable while the timer runs makes `remaining` NaN,
    // which fails every comparison below — the old numbers would stay on screen and the
    // interval would never clear.
    if (Number.isNaN(remaining)) {
      this.#render(0, 0, 0);
      this.#stop();
      return;
    }

    if (remaining <= 0) {
      this.#render(0, 0, 0);
      this.#stop();
      return;
    }

    const minute = 1000 * 60;
    const hour = minute * 60;
    const day = hour * 24;

    this.#render(Math.floor(remaining / day), Math.floor((remaining % day) / hour), Math.floor((remaining % hour) / minute));
  };

  /**
   * @param {number} days
   * @param {number} hours
   * @param {number} mins
   */
  #render(days, hours, mins) {
    this.#renderPart(this.refs.days, days, 'day', 'days');
    this.#renderPart(this.refs.hours, hours, 'hour', 'hours');
    this.#renderPart(this.refs.mins, mins, 'min', 'mins');
  }

  /**
   * @param {HTMLElement | undefined} element
   * @param {number} value
   * @param {string} singular
   * @param {string} plural
   */
  #renderPart(element, value, singular, plural) {
    if (!element) return;

    element.hidden = value <= 0;

    if (value > 0) element.textContent = `${value} ${value === 1 ? singular : plural} `;
  }
}

if (!customElements.get('eq-announcement-countdown')) {
  customElements.define('eq-announcement-countdown', AnnouncementCountdown);
}
